import type { MedicalResult } from '@creanizer/types'
import { Activity, Heart, Thermometer, Droplet, FileText, Pill, Stethoscope } from 'lucide-react'

interface MedicalViewerProps {
  document: MedicalResult
}

export function MedicalViewer({ document }: MedicalViewerProps) {
  // データ検証
  if (!document || !document.header) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">医療記録</h3>
        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
          {JSON.stringify(document, null, 2)}
        </pre>
      </div>
    )
  }
  
  return (
    <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
      {/* ヘッダー（医療機関情報） */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded">
              <Stethoscope className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">{document.header?.facility || '医療機関'}</h1>
              <p className="text-blue-100 text-sm">医療記録</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-blue-100">患者ID</p>
            <p className="font-mono font-bold">{document.header.patientId}</p>
          </div>
        </div>
      </div>
      
      {/* 診察情報 */}
      <div className="p-6">
        {/* 日時情報 */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <div>
            <p className="text-sm text-gray-600">診察日</p>
            <p className="font-medium">{document.header.date}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">診察時間</p>
            <p className="font-medium">{document.header.time}</p>
          </div>
        </div>
        
        {/* 主訴 */}
        {document.chiefComplaint && (
          <div className="mb-6">
            <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              主訴
            </h3>
            <div className="ml-7 p-3 bg-gray-50 rounded">
              <p className="text-gray-700">{document.chiefComplaint}</p>
            </div>
          </div>
        )}
        
        {/* SOAP形式 */}
        <div className="space-y-4">
          {/* S: 主観的所見 */}
          {document.subjective && (
            <div>
              <h3 className="font-bold text-gray-900 mb-2">
                <span className="inline-block w-8 h-8 bg-blue-100 text-blue-700 rounded-full text-center leading-8 mr-2">S</span>
                主観的所見 (Subjective)
              </h3>
              <div className="ml-10 p-3 bg-blue-50 rounded">
                <p className="text-gray-700">{document.subjective}</p>
              </div>
            </div>
          )}
          
          {/* O: 客観的所見 */}
          {document.objective && (
            <div>
              <h3 className="font-bold text-gray-900 mb-2">
                <span className="inline-block w-8 h-8 bg-green-100 text-green-700 rounded-full text-center leading-8 mr-2">O</span>
                客観的所見 (Objective)
              </h3>
              <div className="ml-10 p-3 bg-green-50 rounded">
                <p className="text-gray-700">{document.objective}</p>
              </div>
            </div>
          )}
          
          {/* バイタルサイン */}
          {document.vitalSigns && (
            <div className="ml-10 mt-2">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-100 rounded">
                {document.vitalSigns.bloodPressure && (
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-red-500" />
                    <div>
                      <p className="text-xs text-gray-600">血圧</p>
                      <p className="font-medium">{document.vitalSigns.bloodPressure}</p>
                    </div>
                  </div>
                )}
                {document.vitalSigns.pulse && (
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-600">脈拍</p>
                      <p className="font-medium">{document.vitalSigns.pulse}</p>
                    </div>
                  </div>
                )}
                {document.vitalSigns.temperature && (
                  <div className="flex items-center gap-2">
                    <Thermometer className="w-4 h-4 text-yellow-500" />
                    <div>
                      <p className="text-xs text-gray-600">体温</p>
                      <p className="font-medium">{document.vitalSigns.temperature}</p>
                    </div>
                  </div>
                )}
                {document.vitalSigns.respiration && (
                  <div className="flex items-center gap-2">
                    <Droplet className="w-4 h-4 text-blue-500" />
                    <div>
                      <p className="text-xs text-gray-600">呼吸</p>
                      <p className="font-medium">{document.vitalSigns.respiration}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* A: 評価 */}
          {document.assessment && (
            <div>
              <h3 className="font-bold text-gray-900 mb-2">
                <span className="inline-block w-8 h-8 bg-yellow-100 text-yellow-700 rounded-full text-center leading-8 mr-2">A</span>
                評価 (Assessment)
              </h3>
              <div className="ml-10 p-3 bg-yellow-50 rounded">
                <p className="text-gray-700">{document.assessment}</p>
              </div>
            </div>
          )}
          
          {/* P: 計画 */}
          {document.plan && (
            <div>
              <h3 className="font-bold text-gray-900 mb-2">
                <span className="inline-block w-8 h-8 bg-purple-100 text-purple-700 rounded-full text-center leading-8 mr-2">P</span>
                計画 (Plan)
              </h3>
              <div className="ml-10 p-3 bg-purple-50 rounded">
                <p className="text-gray-700">{document.plan}</p>
              </div>
            </div>
          )}
        </div>
        
        {/* 処方 */}
        {document.prescriptions && document.prescriptions.length > 0 && (
          <div className="mt-6">
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Pill className="w-5 h-5 text-green-600" />
              処方箋
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left">薬剤名</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">用量</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">頻度</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">期間</th>
                  </tr>
                </thead>
                <tbody>
                  {document.prescriptions.map((prescription, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2">{prescription.medication}</td>
                      <td className="border border-gray-300 px-4 py-2">{prescription.dosage}</td>
                      <td className="border border-gray-300 px-4 py-2">{prescription.frequency}</td>
                      <td className="border border-gray-300 px-4 py-2">{prescription.duration}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* 医師署名 */}
        <div className="mt-8 pt-4 border-t border-gray-200">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-sm text-gray-600">担当医師</p>
              <p className="font-medium text-lg">{document.physician.name}</p>
              {document.physician.license && (
                <p className="text-sm text-gray-500">医師免許番号: {document.physician.license}</p>
              )}
            </div>
            {document.physician.signature && (
              <div>
                <p className="text-sm text-gray-600 mb-1">署名</p>
                <p className="font-signature text-2xl text-gray-700">
                  {document.physician.signature}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <style>{`
        .font-signature {
          font-family: 'Courier New', monospace;
          font-style: italic;
        }
      `}</style>
    </div>
  )
}